import { BorrowMarketsCards } from '@/components/borrow/borrowMarketsCards'
import { BorrowMarketsFilters } from '@/components/borrow/borrowMarketsFilters'
import { BorrowMarketsFiltersModal } from '@/components/borrow/borrowMarketsFiltersModal'
import { BorrowMarketsSearchInput } from '@/components/borrow/borrowMarketsSearchInput'
import { BorrowMarketsTable } from '@/components/borrow/borrowMarketsTable'
import { ScrollArea, ScrollBar } from '@/components/common/scrollArea'
import { Content, Page } from '@/components/layout/wrappers'
import { queryClient } from '@/config/query/queryClient'
import { PAGE_KEY } from '@/constants/state'
import { useMixpanel } from '@/context/mixpanel'
import { useBorrowMarkets } from '@/hooks/useBorrowMarkets'
import { useBorrowMarketsSearchState } from '@/hooks/useBorrowMarketsSearchState'
import { useBorrowMarketsSortState } from '@/hooks/useBorrowMarketsSortState'
import { useFilteredBorrowMarkets } from '@/hooks/useFilteredBorrowMarkets'
import { useSortedBorrowMarkets } from '@/hooks/useSortedBorrowMarkets'
import { borrowMarketsQueryOptions, useBorrowMarketsQuery } from '@/queries/borrowMarkets.query'
import { getMetaTags } from '@/utils/meta/getMetaTags'
import { useEffect } from 'react'
import type { MetaFunction } from 'react-router'
import { useLoaderData } from 'react-router'

const BORROW_MARKETS_PER_PAGE = 30

export const loader = async () => {
  const initialBorrowMarkets = await queryClient.ensureQueryData(borrowMarketsQueryOptions())

  return { initialBorrowMarkets }
}

export const shouldRevalidate = ({
  currentUrl,
  nextUrl,
  defaultShouldRevalidate,
}: {
  currentUrl: URL
  nextUrl: URL
  defaultShouldRevalidate: boolean
}) => {
  if (currentUrl.pathname === nextUrl.pathname) {
    return false
  }

  return defaultShouldRevalidate
}

export const meta: MetaFunction = ({ location }) => {
  return getMetaTags({
    title: 'Borrow',
    description: 'Discover borrow opportunities on vaults.fyi.',
    url: location.pathname,
  })
}

export default function Borrow() {
  const { initialBorrowMarkets } = useLoaderData<typeof loader>()
  const { data: borrowMarketsData } = useBorrowMarketsQuery({ initialData: initialBorrowMarkets })
  const { trackEvent } = useMixpanel()
  const { borrowMarkets } = useBorrowMarkets(borrowMarketsData)
  const searchState = useBorrowMarketsSearchState()
  const { page, updateSearchState } = searchState
  const { sortState, handleSetSortBy } = useBorrowMarketsSortState()
  const filteredBorrowMarkets = useFilteredBorrowMarkets({ borrowMarkets, ...searchState })
  const sortedBorrowMarkets = useSortedBorrowMarkets({ borrowMarkets: filteredBorrowMarkets, ...sortState })
  const totalPages = Math.max(1, Math.ceil(sortedBorrowMarkets.length / BORROW_MARKETS_PER_PAGE))
  const effectivePage = Math.min(page, totalPages)

  useEffect(() => {
    if (page !== effectivePage) {
      updateSearchState([PAGE_KEY, effectivePage])
    }
  }, [page, effectivePage, updateSearchState])

  const firstItemOnPageIndex = (effectivePage - 1) * BORROW_MARKETS_PER_PAGE
  const lastItemOnPageIndex = firstItemOnPageIndex + BORROW_MARKETS_PER_PAGE
  const pagedBorrowMarkets = sortedBorrowMarkets.slice(firstItemOnPageIndex, lastItemOnPageIndex)

  return (
    <Page className="max-md:bg-page">
      <ScrollArea className="h-full">
        <Content>
          <div className="flex flex-col gap-4 md:gap-6">
            <BorrowMarketsFilters
              borrowMarkets={borrowMarkets}
              numberOfBorrowOptions={sortedBorrowMarkets.length}
              className="hidden md:flex"
            />
            <div className="flex gap-2 md:hidden">
              <BorrowMarketsSearchInput
                numberOfBorrowOptions={sortedBorrowMarkets.length}
                className="min-w-0 flex-1 [&>input]:bg-surface"
                trackingParams={{ Page: 'Borrow', Device: 'Mobile' }}
              />
              <BorrowMarketsFiltersModal
                borrowMarkets={borrowMarkets}
                numberOfBorrowOptions={sortedBorrowMarkets.length}
                className="h-10 w-10 min-w-10"
              />
            </div>
            <BorrowMarketsTable
              borrowMarkets={pagedBorrowMarkets}
              sortState={sortState}
              onSortByClick={(column) => {
                handleSetSortBy(column)
                trackEvent('Borrow markets table column clicked', {
                  column,
                  sortBy: sortState.sortBy,
                  asc: sortState.asc,
                })
              }}
              currentPage={effectivePage}
              itemsCount={sortedBorrowMarkets.length}
              itemsPerPage={BORROW_MARKETS_PER_PAGE}
              className="hidden md:block"
            />
            <BorrowMarketsCards
              borrowMarkets={pagedBorrowMarkets}
              currentPage={effectivePage}
              itemsCount={sortedBorrowMarkets.length}
              itemsPerPage={BORROW_MARKETS_PER_PAGE}
              className="md:hidden"
            />
          </div>
        </Content>
        <ScrollBar orientation="vertical" />
      </ScrollArea>
    </Page>
  )
}
